import { isActivityType, isProvenanceEntryKind } from "@ondo/shared";
import type { ActivityType, ProvenanceEntryKind } from "@ondo/shared";

import { HISTORY_KINDS } from "../provenance/provenance.constants.ts";

/**
 * What a recorded act is CALLED, wherever it is shown.
 *
 * Shared rather than owned by a screen because two now render the same rows —
 * the dashboard's recent-activity panel and the collector's activity & audit
 * log — and an act that read "Exhibition recorded" on one and something else on
 * the other would be a bug nobody could see from inside either file. It lives in
 * `lib/` for the reason `names.ts` and `choices.ts` do: one definition, because
 * two that must agree are one that will eventually disagree.
 *
 * Topic-scoped rather than folded into `lib/constants.ts`. That file is a
 * grab-bag of short label maps; these tables carry the argument for their own
 * wording, and burying sixty lines of it there would lose the reasoning.
 */

/**
 * What each recorded act is called on the activity list.
 *
 * A `Record` over `ActivityType` rather than a lookup with a fallback, so a
 * fourth activity type cannot arrive without a name — it becomes a compile
 * error here. What arrives over the WIRE is a different matter: see
 * `describeType` in the model for the row that has no name in this build.
 */
export const ACTIVITY_TITLES: Record<ActivityType, string> = {
  "collection.created": "Collection created",
  "collection.shared": "Collection shared",
  "work.registered": "Artwork registered",
  /**
   * Deliberately does not name the agent, and cannot.
   *
   * `payloadFor` withholds `agentOndoId` from this activity, because the work
   * feed reaches every reader of the work while `work_agent_mandates_read`
   * shows the mandate to the owner and the agent alone. So the line says
   * representation changed and the record itself says by whom — to the two
   * people entitled to know.
   */
  "work.agent_assigned": "Agent assigned",
  "work.agent_removed": "Agent mandate ended",
  /**
   * Neutral about who did what, for the reason the verification titles below
   * are: one recorded line lands in both parties' trails, so "I asked" and "I
   * was asked" are the same row read by different people — `describeType` in
   * the model is where the reader's own id tells them apart.
   *
   * The answer does NOT say which way it went, and that is deliberate rather
   * than missing. `payloadFor` carries `granted`, so the fact is in the feed;
   * what is not in the feed is anything naming the other party, and a title
   * reading "Representation declined" on a trail beside an actor's name would
   * put a refusal of somebody's professional approach into a line other people
   * read. The request itself says which way it went, to the two people in it.
   */
  "work.representation_requested": "Representation requested",
  "work.representation_answered": "Representation request answered",
  /**
   * Not "Provenance verified". An entry added to a work's history is a claim its
   * author makes; what the registry has to say about it is who put their name
   * behind it, and that is a count on the entry rather than a line here.
   *
   * THE FALLBACK, and it is reached more often than it looks. Every row written
   * before 20260820090100 carries no kind, and so does one written by a build
   * that knows a ninth. `HISTORY_ACTIVITY_TITLES` below is the specific version.
   */
  /**
   * Deliberately does not name the gallery, and cannot.
   *
   * `payloadFor` withholds `counterpartyOndoId` for the reason it withholds
   * `agentOndoId`: the work feed reaches every reader of the work, while
   * `consignments_read` shows the row to the gallery and to whoever may share
   * the work. Which dealer is holding a picture is a commercial fact and a
   * physical-security one, and neither belongs in a fan-out.
   *
   * "Consignment ended" says nothing about how, for the same reason the
   * representation answer says nothing about which way it went — and one more
   * here: an outcome of `sold` is a CLAIM, and a line reading "Sold" would
   * report an act this registry cannot yet perform.
   */
  "consignment.offered": "Consignment offered",
  "consignment.accepted": "Consignment accepted",
  "consignment.ended": "Consignment ended",
  /**
   * "Terms revised" and emphatically not "Price changed".
   *
   * The line names what moved and not what it moved to, for the reason the three
   * above name neither party: `payloadFor` carries no figure, so a title that
   * promised one would be a sentence the row cannot finish. It also reads as the
   * arrangement continuing, which is the whole of OND-101 — the trail used to
   * show a repricing as a consignment ending and another beginning.
   */
  "consignment.terms_revised": "Consignment terms revised",
  /**
   * "Sold" — and this is the line the sentence above was holding the word back
   * for.
   *
   * A consignment ending with an outcome of `sold` is a claim, so its title
   * stays "Consignment ended". This one has an `ondo.transfers` row behind it,
   * which is what earns the word. Both are recorded when a work sells, and they
   * read as two lines because they are about two rows.
   *
   * NAMES NEITHER PARTY, for the reason the consignment titles cannot name a
   * gallery: `payloadFor` withholds both ONDO ids, because the work feed reaches
   * every reader of the work and `transfers_read` has no viewer branch at all.
   * The PRICE is withheld for the same reason and matters more — it is the one
   * fact on this row that a feed could quietly publish out from under its
   * policy.
   */
  /**
   * Possession, never title — and deliberately no venue.
   *
   * The missing venue is what a reader of this file will notice first: "Loaned
   * to Tate Modern" is the sentence a trail wants, and `payloadFor` withholds
   * it. Where an object physically is has been the sensitive fact in this
   * feature since the migration that created the table — "a valuer shown a
   * single work must not learn that it sits in a warehouse in Geneva" — and the
   * work feed reaches that valuer. The loans screen says where, to the owner,
   * through `ondo.owner_loans()`.
   *
   * The first two are read from BOTH SIDES of the same line, like the
   * verification titles below: a proposal is fanned out to the lender's feed
   * and the borrower's and to nowhere else, so these say what happened and the
   * actor's name beside the row says who did it.
   *
   * The ending says that it ended and not how, the restraint every ending in
   * this file keeps. `payloadFor` carries the outcome, so a party to the loan
   * can be told which it was; a trail other people read says the arrangement
   * finished and stops.
   */
  "loan.proposed": "Loan proposed",
  "loan.withdrawn": "Loan proposal withdrawn",
  "loan.agreed": "Loan agreed",
  // NAMES NOBODY, which is the restraint every verb in this file keeps and
  // which matters more here than anywhere: a line reading "Kunstmuseum Basel
  // declined" would publish an institution's decision to whoever can read the
  // trail. `feedsFor` keeps this verb to the two parties for the same reason,
  // so the sentence only ever reaches people who already know who declined.
  "loan.declined": "Loan proposal declined",
  "loan.dispatched": "Work sent out on loan",
  // SAYS IT ARRIVED AND NOT WHERE. The venue is the sensitive fact in this
  // feature and no loan payload carries one, so there is nothing for this
  // sentence to disclose even by accident.
  "loan.received": "Work received on loan",
  "loan.ended": "Loan ended",
  "work.sold": "Sold",
  "work.title_transferred": "Ownership transferred",
  /**
   * Says the sale ended and not how, which is the same restraint every ending
   * in this file keeps. `payloadFor` carries the outcome, so a reader who is a
   * party to the row can be told; a trail other people read says that the sale
   * did not stand and stops.
   */
  "work.sale_ended": "Sale ended",
  /**
   * NAMES NO FIGURE, and cannot — `payloadFor` carries none. The work feed
   * reaches every reader of the picture, while a settlement's money is
   * disclosed by one narrow definer to the covenant holder and the artist. So
   * the line says a covenant fell due and the screen says how much, to the two
   * parties entitled to know.
   */
  "settlement.due": "Resale settlement due",
  "settlement.settled": "Resale settlement paid",
  "settlement.answered": "Resale settlement answered",
  "provenance.added": "Artwork history recorded",
  /**
   * Read from BOTH sides of the same activity.
   *
   * A verification is fanned out to the asker's feed and the asked party's, so
   * one recorded line is what each of them sees — "I asked" and "I was asked"
   * are the same row read by different people. These titles are therefore
   * deliberately neutral about who did what: `describeType` in the model has no
   * way to know, and the actor's name sits beside the line already.
   */
  "provenance.verification_requested": "Verification asked for",
  "provenance.verified": "Provenance verified",
  "provenance.disputed": "Provenance disputed",
  "provenance.verification_declined": "Verification declined",
  "provenance.verification_referred": "Verification passed on",
  /**
   * The one line here that is ONDO apologising rather than reporting — OND-205.
   *
   * IN A PERSON'S WORDS AND NOT AN ERROR STRING, which is the whole of the
   * ticket. `ondo.notification_digests.last_error` holds a transport code or a
   * rejected mailbox, and `payloadFor` refuses to carry it for the reason
   * `settlement.due` refuses to carry a figure — so there is nothing here that
   * could print it even by accident. "Could not be emailed" says the one thing
   * the reader can do something about: look somewhere else.
   *
   * "EMAILED" RATHER THAN "SENT", because the summary is still available and
   * saying it was not sent would be wider than the truth. Every notification it
   * would have carried is in Notifications, unread and waiting — the digest is a
   * convenience over an inbox that is already complete — and
   * `ACTIVITY_COPY.notInbox` on this screen already points there.
   *
   * The SECOND LINE is the morning it was for, "7 October 2026", which arrives
   * on `subject.label` already in words. This screen needs no date formatting
   * for it and neither does the phone, which is `20261008091000`'s reason for
   * formatting it in SQL: two copies of one sentence drift.
   */
  "digest.missed": "Daily summary could not be emailed",
};

/**
 * The same line, said specifically, when the activity carries a kind.
 *
 * "Exhibition recorded" beats "Artwork history recorded" for the reason an audit
 * trail exists at all — eleven identical lines are a list somebody scrolls past.
 * A kind this build does not recognise reads as null on the wire and falls back
 * to the general sentence above rather than failing: a feed outlives the
 * deployment that wrote it.
 *
 * The glyph comes from `HISTORY_KINDS`, which is also what the timeline draws,
 * so a kind cannot wear one mark on the record and another on the trail.
 */
export const HISTORY_ACTIVITY_TITLES: Record<ProvenanceEntryKind, string> = {
  sale: "Change of ownership recorded",
  auction: "Auction recorded",
  loan: "Loan recorded",
  exhibition: "Exhibition recorded",
  location: "Change of location recorded",
  // OND-38's word, on the trail as well as on the chip. The kind is still
  // `bibliography` on the wire and in the schema; "significant publication" is
  // what the client calls it, and a trail that called it something else would
  // be the one place a reader met the old noun.
  bibliography: "Significant publication recorded",
  document: "Document recorded",
  condition: "Condition report recorded",
  insurance: "Insurance recorded",
  /**
   * "Recorded", like every other line here, and deliberately not "claimed" or
   * "attributed". The trail says what happened: somebody put an authorship claim
   * on the record. Whether anybody with authority agrees with it is the
   * attribution state on the work itself, and a feed may be silently lost — so a
   * feed line must never be the thing that says a work is authenticated.
   */
  attribution: "Artist attribution recorded",
};

/**
 * What a verification says when it knows WHICH link it was about.
 *
 * Composed with `HISTORY_KINDS[kind].chip` rather than written out as five
 * records of eight — "Exhibition" + " verified" is the same sentence as a
 * 40-entry table and cannot drift from the chip the modal draws. So a trail
 * reads *Condition report verified* and *Sale disputed* instead of nine lines
 * that say "Provenance verified" and leave the reader to open each one.
 *
 * The five verbs that are about an entry are exactly the five that carry a
 * kind. Anything else has none, and `describeType` never reaches this.
 */
export const VERIFICATION_OF_KIND: Partial<Record<ActivityType, string>> = {
  "provenance.verification_requested": "verification asked for",
  "provenance.verified": "verified",
  "provenance.disputed": "disputed",
  "provenance.verification_declined": "verification declined",
  "provenance.verification_referred": "verification passed on",
};

/**
 * Who did it, in front of the name.
 *
 * Separated from the name itself because the name is not always what is
 * printed: an activity the reader performed says "by you", which is the only
 * thing `actor === my own ONDO id` is for on this screen. A feed is fanned out
 * to both parties, so the same recorded line is read from two sides and only
 * the reader's own id can tell them apart.
 */
export const ACTIVITY_BY = {
  prefix: "by",
  self: "you",
} as const;

/** The glyph beside each act. Reuses the set already on this screen's quick actions. */
export const ACTIVITY_GLYPHS: Record<ActivityType, string> = {
  "collection.created": "◫",
  "collection.shared": "⇢",
  "work.registered": "✦",
  "work.agent_assigned": "⇄",
  "work.agent_removed": "⇄",
  "work.representation_requested": "↗",
  "work.representation_answered": "↩",
  "consignment.offered": "↗",
  "consignment.accepted": "▤",
  "consignment.ended": "—",
  // The same glyph as the acceptance, because it is the same state: the gallery
  // is holding the work on agreed terms. A glyph of its own would make a
  // repricing look like a different kind of event in a column read at a glance.
  "consignment.terms_revised": "▤",
  "loan.proposed": "↗",
  "loan.withdrawn": "—",
  "loan.agreed": "▤",
  "loan.declined": "—",
  "loan.dispatched": "→",
  "loan.received": "▣",
  "loan.ended": "↩",
  "work.sold": "→",
  "work.title_transferred": "⇄",
  "work.sale_ended": "—",
  "settlement.due": "⇄",
  "settlement.settled": "£",
  "settlement.answered": "↩",
  "provenance.added": "◈",
  "provenance.verification_requested": "↗",
  "provenance.verified": "✓",
  "provenance.disputed": "✕",
  "provenance.verification_declined": "—",
  "provenance.verification_referred": "↪",
  // AN ENVELOPE THAT DID NOT ARRIVE. Not the "—" three endings share: those
  // mark something that finished, and this marks something that never happened.
  // Not "✕" either, which `provenance.disputed` holds for a party contradicting
  // the record — a reader scanning this column should not see ONDO's own
  // delivery fault wearing the mark of a dispute.
  "digest.missed": "✉",
};

/**
 * What a row says, and the two ways it can be wider than this build.
 *
 * An act with no name here is dropped — recorded history outlives the code that
 * reads it. A history KIND with no name is not: the act is still one this build
 * knows, so the row reads under the general sentence rather than disappearing.
 * The difference matters. Dropping the row would hide a real thing that happened
 * to the collector's work; saying "Artwork history recorded" instead of
 * "Exhibition recorded" only says less.
 */
export function describeAct(
  type: string,
  historyKind: ProvenanceEntryKind | null,
): { title: string; glyph: string } | null {
  if (!isActivityType(type)) return null;

  // `isProvenanceEntryKind` as well as the null check, and not belt-and-braces:
  // a feed outlives the build that wrote it, so `historyKind` can name a kind
  // this deployment has never heard of. The API normalises one it does not know
  // to null, but this function is the shared one and must not depend on a
  // guarantee made two layers away — an unrecognised kind reads under the
  // general sentence rather than taking the row, or the screen, down.
  if (type === "provenance.added" && isProvenanceEntryKind(historyKind)) {
    return {
      title: HISTORY_ACTIVITY_TITLES[historyKind],
      glyph: HISTORY_KINDS[historyKind].glyph,
    };
  }

  // A verification names the link it was about. The GLYPH stays the verb's, not
  // the kind's: on this list the reader is scanning for what happened, and a ✓
  // beside "Condition report verified" says more than the condition report's own
  // mark repeated from the timeline.
  const act = VERIFICATION_OF_KIND[type];
  if (act !== undefined && isProvenanceEntryKind(historyKind)) {
    return {
      title: `${HISTORY_KINDS[historyKind].chip} ${act}`,
      glyph: ACTIVITY_GLYPHS[type],
    };
  }

  return { title: ACTIVITY_TITLES[type], glyph: ACTIVITY_GLYPHS[type] };
}
